import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip, Typography, message, MenuProps, Modal } from "antd";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import { ToastSkin } from "../../context/ToastContext";
import {
    CreateV2Automation,
    DeleteV2Automation,
    EnsureV2AutomationDraftExitCondition,
    GetV2Automation,
    GetV2AutomationDraftSettings,
    GetV2AutomationSteps,
    ListV2Automations,
    UpdateV2AutomationActive,
    UpdateV2Automation,
    UpsertV2AutomationDraftSettings,
    V2AutomationListItemDto,
} from "../../api/v2/automations";
import AutomationTestLabModal from "./AutomationTestLabModal";
import V2Tag from "../../components/v2/V2Tag";
import Page from "../../components/page/Page";
import Table from "../../components/table/Table";
import { ColProps, colTypeOptions, FetchProps, FilterProps, filterTypeOptions } from "../../components/table/tableTypes";
import StatusDot from "../../components/statusDot/StatusDot";
import { exportToCsv } from "../../utils/exportToCsv";
import { formatDate } from "../../utils/friendlyDate";
import AutomationTreeEditor from "./AutomationTreeEditor";
import { CreateV2AutomationTrigger, GetV2AutomationTriggers, V2TriggerDto } from "../../api/v2/triggers";
import ConditionTreeEditorModal from "./ConditionsTreeEditorModal";
import AutomationEditorPanel, { COOLDOWN_PRESETS, CooldownUnit } from "./components/automationEditorPanel/AutomationEditorPanel";
import "./automations-list-page.css";

const { Text } = Typography;

const COOLDOWN_DEFAULT_SECONDS = 7 * 24 * 60 * 60;

function splitCooldown(seconds: number): { value: number; unit: CooldownUnit } {
    const safe = Math.max(0, Number(seconds || 0));
    if (safe % (24 * 60 * 60) === 0) return { value: safe / (24 * 60 * 60), unit: "days" };
    if (safe % (60 * 60) === 0) return { value: safe / (60 * 60), unit: "hours" };
    return { value: Math.max(0, Math.floor(safe / 60)), unit: "minutes" };
}

function toSeconds(value: number, unit: CooldownUnit): number {
    const safe = Math.max(0, Number(value || 0));
    if (unit === "days") return safe * 24 * 60 * 60;
    if (unit === "hours") return safe * 60 * 60;
    return safe * 60;
}

function formatShortDate(v?: string | null) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
    }).format(d);
}

function formatDateTime(v?: string | null) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).format(d);
}

export default function AutomationsListPage() {
    const { selectedDomain } = useUserContext();
    const { showToast, closeToast } = useToast();
    const [refreshKey, setRefreshKey] = useState(0);
    const [testLabOpen, setTestLabOpen] = useState(false);
    const [testLabAutomationId, setTestLabAutomationId] = useState<number | null>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelLoading, setPanelLoading] = useState(false);
    const [selectedAutomationId, setSelectedAutomationId] = useState<number | null>(null);
    const [automationName, setAutomationName] = useState("");
    const [automationDescription, setAutomationDescription] = useState("");
    const [automationActive, setAutomationActive] = useState<0 | 1>(1);
    const [primaryTrigger, setPrimaryTrigger] = useState<V2TriggerDto | null>(null);
    const [triggerCooldownSeconds, setTriggerCooldownSeconds] = useState(COOLDOWN_DEFAULT_SECONDS);
    const [triggerMaxActiveRuns, setTriggerMaxActiveRuns] = useState(1);
    const [savingPanel, setSavingPanel] = useState(false);
    const [autoSaving, setAutoSaving] = useState(false);
    const [isNewAutomationDraft, setIsNewAutomationDraft] = useState(false);
    const [treeModalOpen, setTreeModalOpen] = useState(false);
    const [treeFullscreen, setTreeFullscreen] = useState(false);
    const [hasPublishableDraftChanges, setHasPublishableDraftChanges] = useState(false);
    const [hasEntryConditionDraftChanges, setHasEntryConditionDraftChanges] = useState(false);
    const [exitConditionModalOpen, setExitConditionModalOpen] = useState(false);
    const [exitConditionModalId, setExitConditionModalId] = useState<number | null>(null);
    const [cooldownMode, setCooldownMode] = useState<string>("custom");
    const [cooldownCustomValue, setCooldownCustomValue] = useState<number>(0);
    const [cooldownCustomUnit, setCooldownCustomUnit] = useState<CooldownUnit>("minutes");
    const [draftExitConditionId, setDraftExitConditionId] = useState<number | null>(null);
    const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSavedPanelSnapshotRef = useRef<string>("");
    const lastPublishedPanelSnapshotRef = useRef<string>("");

    const normalizeGraphSnapshot = (graph: any) => {
        const steps = Array.isArray(graph?.steps) ? graph.steps : [];
        const edges = Array.isArray(graph?.edges) ? graph.edges : [];
        const normSteps = [...steps]
            .map((s: any) => ({
                step_index: Number(s?.step_index),
                step_type: String(s?.step_type || ""),
                config: s?.config ?? null,
            }))
            .sort((a, b) => a.step_index - b.step_index);
        const normEdges = [...edges]
            .map((e: any) => ({
                from_step_index: Number(e?.from_step_index),
                to_step_index: Number(e?.to_step_index),
                edge_key: String(e?.edge_key || "default"),
                sort_order: Number(e?.sort_order || 0),
            }))
            .sort((a, b) => {
                if (a.from_step_index !== b.from_step_index) return a.from_step_index - b.from_step_index;
                if (a.to_step_index !== b.to_step_index) return a.to_step_index - b.to_step_index;
                if (a.edge_key !== b.edge_key) return a.edge_key.localeCompare(b.edge_key);
                return a.sort_order - b.sort_order;
            });
        return JSON.stringify({ steps: normSteps, edges: normEdges });
    };

    const refreshPublishableDraftState = async (automationId: number) => {
        if (!selectedDomain || !automationId) {
            setHasPublishableDraftChanges(false);
            return;
        }
        const [draftRes, publishedRes] = await Promise.all([
            GetV2AutomationSteps(selectedDomain, automationId, "draft"),
            GetV2AutomationSteps(selectedDomain, automationId, "published"),
        ]);
        const graphDraftChanged =
            !draftRes.ok || !publishedRes.ok
                ? true
                : normalizeGraphSnapshot(draftRes.data) !== normalizeGraphSnapshot(publishedRes.data);

        const panelDraftChanged =
            !!lastPublishedPanelSnapshotRef.current &&
            lastSavedPanelSnapshotRef.current !== lastPublishedPanelSnapshotRef.current;

        setHasPublishableDraftChanges(graphDraftChanged || panelDraftChanged);
    };

    const getPanelSnapshot = (params: {
        name: string;
        description: string;
        cooldown: number;
        maxActive: number;
        exitConditionId: number | null;
    }) =>
        JSON.stringify({
            name: String(params.name || "").trim(),
            description: String(params.description || "").trim(),
            cooldown: Math.max(0, Number(params.cooldown || 0)),
            maxActive: Math.max(1, Number(params.maxActive || 1)),
            exitConditionId: params.exitConditionId != null ? Number(params.exitConditionId) : null,
        });

    const statusOptions: MenuProps["items"] = [
        { key: "active", label: "Active" },
        { key: "inactive", label: "Inactive" },
    ];

    const tableFilters: FilterProps[] = [
        { filterKey: "search", name: "Search", type: filterTypeOptions.text },
        { filterKey: "status", name: "Status", type: filterTypeOptions.textToKey, options: statusOptions, allLabel: "All Statuses", selected: "all" },
    ];

    const cols: ColProps[] = [
        {
            key: "active",
            name: "",
            type: colTypeOptions.text,
            width: 28,
            paddingLeft: 0,
            render: (v: number | boolean) => {
                const isActive = !!v;
                return (
                    <div className="v2-automation-status-cell">
                        <StatusDot status={isActive ? "enabled" : "disabled"} />
                    </div>
                );
            },
        },
        {
            key: "name",
            name: "Name",
            type: colTypeOptions.text,
            width: 250,
            render: (v: string) => <Text>{v}</Text>,
        },
        {
            key: "runs_count",
            name: "Runs",
            type: colTypeOptions.number,
            width: 90,
            render: (v: number | undefined) => Number(v || 0).toLocaleString(),
        },
        {
            key: "id",
            name: "Last Run",
            type: colTypeOptions.text,
            width: 160,
            render: (_: number, row: V2AutomationListItemDto) => {
                const status = String(row.last_run_status || "").toLowerCase();
                if (!status) return "-";
                const color =
                    status === "completed" ? "green" :
                    status === "active" ? "blue" :
                    status === "error" ? "red" :
                    status === "cancelled" ? "default" :
                    status === "paused" ? "gold" : "default";
                if (status === "completed" && row.last_run_entered_at) {
                    return (
                        <Tooltip title={formatDateTime(row.last_run_entered_at)}>
                            <span>
                                <V2Tag color={color}>{row.last_run_status}</V2Tag>
                            </span>
                        </Tooltip>
                    );
                }
                return <V2Tag color={color}>{row.last_run_status}</V2Tag>;
            },
        },
        {
            key: "created_at",
            name: "Created",
            type: colTypeOptions.text,
            width: 180,
            render: (v: string) => (
                <Tooltip title={formatDateTime(v)}>
                    <span>{formatShortDate(v)}</span>
                </Tooltip>
            ),
        },
        {
            key: "updated_at",
            name: "Updated",
            type: colTypeOptions.text,
            width: 180,
            render: (v: string) => (
                <Tooltip title={formatDateTime(v)}>
                    <span>{formatShortDate(v)}</span>
                </Tooltip>
            ),
        },
    ];

    const loadAutomationPanel = async (automationId: number) => {
        if (!selectedDomain) return;
        setPanelOpen(true);
        setPanelLoading(true);
        try {
            const [automationRes, triggersRes, draftSettingsRes] = await Promise.all([
                GetV2Automation(selectedDomain, automationId),
                GetV2AutomationTriggers(selectedDomain, automationId),
                GetV2AutomationDraftSettings(selectedDomain, automationId),
            ]);

            if (!automationRes.ok || !automationRes.data) {
                message.error(automationRes.error || "Failed to load automation");
                return;
            }

            let trigger: V2TriggerDto | null = null;
            if (triggersRes.ok && Array.isArray(triggersRes.data) && triggersRes.data.length) {
                trigger = triggersRes.data[0];
            } else {
                const createRes = await CreateV2AutomationTrigger(selectedDomain, automationId, {
                    title: "Primary trigger",
                    cooldown_seconds: COOLDOWN_DEFAULT_SECONDS,
                    max_active_runs_per_client: 1,
                    active: 1,
                });
                if (createRes.ok) {
                    const reloadTriggers = await GetV2AutomationTriggers(selectedDomain, automationId);
                    if (reloadTriggers.ok && Array.isArray(reloadTriggers.data) && reloadTriggers.data.length) {
                        trigger = reloadTriggers.data[0];
                    }
                }
            }

            const draftSettings = draftSettingsRes.ok ? draftSettingsRes.data : null;
            const nextName = String(draftSettings?.name ?? automationRes.data.name ?? "");
            const nextDescription = draftSettings?.description != null
                ? String(draftSettings.description)
                : (automationRes.data.description != null ? String(automationRes.data.description) : "");
            const nextActive: 0 | 1 = Number(automationRes.data.active) ? 1 : 0;
            const liveCooldown = Math.max(0, Number(trigger?.cooldown_seconds ?? COOLDOWN_DEFAULT_SECONDS));
            const nextCooldownSeconds = Math.max(0, Number(draftSettings?.cooldown_seconds ?? liveCooldown));
            const liveMaxActive = Math.max(1, Number(trigger?.max_active_runs_per_client || 1));
            const nextMaxActive = Math.max(1, Number(draftSettings?.max_active_runs_per_client ?? liveMaxActive));
            const nextExitConditionId =
                draftSettings?.exit_condition_id != null
                    ? Number(draftSettings.exit_condition_id)
                    : (trigger?.exit_condition_id != null ? Number(trigger.exit_condition_id) : null);

            setSelectedAutomationId(automationId);
            setAutomationName(nextName);
            setAutomationDescription(nextDescription);
            setAutomationActive(nextActive);
            setPrimaryTrigger(trigger);
            setTriggerCooldownSeconds(nextCooldownSeconds);
            const matchedPreset = COOLDOWN_PRESETS.find((p) => p.seconds === nextCooldownSeconds);
            if (matchedPreset) {
                setCooldownMode(matchedPreset.key);
            } else {
                setCooldownMode("custom");
                const split = splitCooldown(nextCooldownSeconds);
                setCooldownCustomValue(split.value);
                setCooldownCustomUnit(split.unit);
            }
            setTriggerMaxActiveRuns(nextMaxActive);
            setDraftExitConditionId(nextExitConditionId);
            setIsNewAutomationDraft(false);
            const livePanelSnapshot = getPanelSnapshot({
                name: String(automationRes.data.name || ""),
                description: String(automationRes.data.description || ""),
                cooldown: liveCooldown,
                maxActive: liveMaxActive,
                exitConditionId: trigger?.exit_condition_id != null ? Number(trigger.exit_condition_id) : null,
            });
            lastPublishedPanelSnapshotRef.current = livePanelSnapshot;
            lastSavedPanelSnapshotRef.current = getPanelSnapshot({
                name: nextName,
                description: nextDescription,
                cooldown: nextCooldownSeconds,
                maxActive: nextMaxActive,
                exitConditionId: nextExitConditionId,
            });
            await refreshPublishableDraftState(automationId);
            setHasEntryConditionDraftChanges(false);
        } finally {
            setPanelLoading(false);
        }
    };

    const saveAutomationPanel = async (opts?: { silent?: boolean; allowCreate?: boolean; refreshTable?: boolean }): Promise<{ ok: boolean; automationId?: number }> => {
        const silent = !!opts?.silent;
        const allowCreate = opts?.allowCreate !== false;
        const refreshTable = opts?.refreshTable !== false;
        if (!selectedDomain) return { ok: false };
        if (silent) setAutoSaving(true);
        else setSavingPanel(true);
        try {
            let automationId = selectedAutomationId;

            if (!automationId) {
                if (!allowCreate) return { ok: false };
                const created = await CreateV2Automation(selectedDomain, {
                    name: String(automationName || "").trim() || "New Automation",
                    description: String(automationDescription || "").trim() || null,
                    active: automationActive,
                });
                if (!created.ok || !created.data?.id) {
                    if (!silent) message.error(created.error || "Failed creating automation");
                    return { ok: false };
                }
                automationId = Number(created.data.id);
                setSelectedAutomationId(automationId);
                setIsNewAutomationDraft(false);
                const createTrigger = await CreateV2AutomationTrigger(selectedDomain, automationId, {
                    title: "Primary trigger",
                    cooldown_seconds: Math.max(0, Number(triggerCooldownSeconds || 0)),
                    max_active_runs_per_client: Math.max(1, Number(triggerMaxActiveRuns || 1)),
                    active: 1,
                });
                if (!createTrigger.ok) {
                    if (!silent) message.error(createTrigger.error || "Failed creating trigger settings");
                    return { ok: false };
                }
                const reloadTriggers = await GetV2AutomationTriggers(selectedDomain, automationId);
                if (reloadTriggers.ok && Array.isArray(reloadTriggers.data) && reloadTriggers.data.length) {
                    setPrimaryTrigger(reloadTriggers.data[0]);
                }
            }

            if (!automationId) return { ok: false };

            const draftSaved = await UpsertV2AutomationDraftSettings(selectedDomain, automationId, {
                name: String(automationName || "").trim() || "Automation",
                description: String(automationDescription || "").trim() || null,
                active: automationActive,
                cooldown_seconds: Math.max(0, Number(triggerCooldownSeconds || 0)),
                max_active_runs_per_client: Math.max(1, Number(triggerMaxActiveRuns || 1)),
                exit_condition_id: draftExitConditionId != null ? Number(draftExitConditionId) : null,
            });
            if (!draftSaved.ok) {
                if (!silent) message.error(draftSaved.error || "Failed saving draft");
                return { ok: false };
            }

            if (!silent) showToast({ skin: ToastSkin.approval, text: "Changes saved" });
            if (refreshTable) setRefreshKey((v) => v + 1);
            lastSavedPanelSnapshotRef.current = getPanelSnapshot({
                name: automationName,
                description: automationDescription,
                cooldown: triggerCooldownSeconds,
                maxActive: triggerMaxActiveRuns,
                exitConditionId: draftExitConditionId,
            });
            setHasPublishableDraftChanges(true);
            return { ok: true, automationId };
        } finally {
            if (silent) setAutoSaving(false);
            else setSavingPanel(false);
        }
    };

    const publishFromPanel = async () => {
        if (!selectedDomain) return;
        setSavingPanel(true);
        try {
            const saved = await saveAutomationPanel({ silent: true, allowCreate: false, refreshTable: false });
            if (!saved.ok || !saved.automationId) return;
            const pub = await UpdateV2Automation(selectedDomain, saved.automationId, {
                name: String(automationName || "").trim() || "Automation",
                description: String(automationDescription || "").trim() || null,
                active: automationActive,
                publish: true,
            });
            if (!pub.ok) {
                message.error(pub.error || "Failed to publish");
                return;
            }
            showToast({ skin: ToastSkin.approval, text: "Published" });
            lastPublishedPanelSnapshotRef.current = getPanelSnapshot({
                name: automationName,
                description: automationDescription,
                cooldown: triggerCooldownSeconds,
                maxActive: triggerMaxActiveRuns,
                exitConditionId: draftExitConditionId,
            });
            lastSavedPanelSnapshotRef.current = lastPublishedPanelSnapshotRef.current;
            if (saved.automationId) {
                await refreshPublishableDraftState(saved.automationId);
                setHasEntryConditionDraftChanges(false);
                setRefreshKey((v) => v + 1);
            }
        } finally {
            setSavingPanel(false);
        }
    };

    const openExitCondition = async () => {
        if (!selectedDomain || !selectedAutomationId) return;
        const ensured = await EnsureV2AutomationDraftExitCondition(selectedDomain, selectedAutomationId);
        if (!ensured.ok) {
            message.error(ensured.error || "Failed preparing exit condition");
            return;
        }
        const ensuredId = Number(ensured.data?.exit_condition_id || draftExitConditionId || 0);
        if (!ensuredId) {
            message.error("Failed preparing exit condition");
            return;
        }
        setDraftExitConditionId(ensuredId);
        setExitConditionModalId(ensuredId);
        setExitConditionModalOpen(true);
        setHasPublishableDraftChanges(true);
    };

    const updateAutomationActiveLive = async (nextActive: 0 | 1) => {
        const prev = automationActive;
        setAutomationActive(nextActive);
        if (!selectedDomain || !selectedAutomationId) return;
        setAutoSaving(true);
        try {
            const out = await UpdateV2AutomationActive(selectedDomain, selectedAutomationId, { active: nextActive });
            if (!out.ok) {
                setAutomationActive(prev);
                message.error(out.error || "Failed updating status");
                return;
            }
            setRefreshKey((v) => v + 1);
        } finally {
            setAutoSaving(false);
        }
    };

    const createNewAutomationAndOpenPanel = async () => {
        setSelectedAutomationId(null);
        setAutomationName("");
        setAutomationDescription("");
        setAutomationActive(1);
        setPrimaryTrigger(null);
        setTriggerCooldownSeconds(COOLDOWN_DEFAULT_SECONDS);
        setTriggerMaxActiveRuns(1);
        setCooldownMode("7d");
        setCooldownCustomValue(7);
        setCooldownCustomUnit("days");
        setDraftExitConditionId(null);
        setIsNewAutomationDraft(true);
        setHasPublishableDraftChanges(false);
        setHasEntryConditionDraftChanges(false);
        lastSavedPanelSnapshotRef.current = "";
        lastPublishedPanelSnapshotRef.current = "";
        setPanelOpen(true);
    };

    const deleteAutomationFromPanel = async () => {
        if (!selectedDomain) return;
        if (!selectedAutomationId) {
            setPanelOpen(false);
            return;
        }
        setSavingPanel(true);
        try {
            const del = await DeleteV2Automation(selectedDomain, selectedAutomationId);
            if (!del.ok) {
                message.error(del.error || "Failed deleting automation");
                return;
            }
            showToast({ skin: ToastSkin.approval, text: "Automation deleted" });
            setPanelOpen(false);
            setTreeModalOpen(false);
            setExitConditionModalOpen(false);
            setSelectedAutomationId(null);
            setDraftExitConditionId(null);
            setRefreshKey((v) => v + 1);
        } finally {
            setSavingPanel(false);
        }
    };

    useEffect(() => {
        if (!panelOpen || panelLoading || isNewAutomationDraft || !selectedAutomationId || savingPanel || autoSaving) return;
        const snapshot = getPanelSnapshot({
            name: automationName,
            description: automationDescription,
            cooldown: triggerCooldownSeconds,
            maxActive: triggerMaxActiveRuns,
            exitConditionId: draftExitConditionId,
        });
        if (snapshot === lastSavedPanelSnapshotRef.current) return;
        if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = setTimeout(() => {
            saveAutomationPanel({ silent: true, allowCreate: false, refreshTable: false });
        }, 600);
        return () => {
            if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
        };
    }, [
        panelOpen,
        panelLoading,
        isNewAutomationDraft,
        selectedAutomationId,
        automationName,
        automationDescription,
        triggerCooldownSeconds,
        triggerMaxActiveRuns,
        draftExitConditionId,
        savingPanel,
        autoSaving,
    ]);

    useEffect(() => {
        if (!selectedAutomationId || treeModalOpen) return;
        void refreshPublishableDraftState(selectedAutomationId);
    }, [treeModalOpen, selectedAutomationId]);

    const fetchTableData = async ({ isCsvCall, page = 0, pageSize = 100, filters = [] }: FetchProps) => {
        if (!selectedDomain) return { data: { report: [], total: 0 } };
        const searchFilter = filters.find((f: any) => f?.filterKey === "search");
        const statusFilter = filters.find((f: any) => f?.filterKey === "status");
        const q = String(searchFilter?.value || "").trim().toLowerCase();
        const status = String(statusFilter?.selected || "all");
        const normalizedStatus = status === "active" || status === "inactive" ? status : "all";

        if (isCsvCall) {
            const batchSize = 200;
            let offset = 0;
            let allRows: V2AutomationListItemDto[] = [];
            let total = 0;

            // Pull all pages for export with current filters.
            do {
                const csvRes = await ListV2Automations(selectedDomain, {
                    q,
                    status: normalizedStatus,
                    limit: batchSize,
                    offset,
                });
                if (!csvRes.ok) {
                    message.error(csvRes.error || "Failed to export automations");
                    return;
                }
                const batch = csvRes.data?.automations || [];
                total = Number(csvRes.data?.total || 0);
                allRows = allRows.concat(batch);
                offset += batchSize;
            } while (allRows.length < total);

            exportToCsv(
                allRows,
                `Vuelve Automations - ${selectedDomain} - ${formatDate(new Date())}`
            );
            return;
        }

        const safePage = Math.max(0, Number(page) || 0);
        const safePageSize = Math.max(1, Number(pageSize) || 100);
        const offset = safePage * safePageSize;
        const res = await ListV2Automations(selectedDomain, {
            q,
            status: normalizedStatus,
            limit: safePageSize,
            offset,
        });
        if (!res.ok) {
            message.error(res.error || "Failed to load automations");
            return { data: { report: [], total: 0 } };
        }
        const report = (res.data?.automations || []).map((r) => ({ ...r, actions: r.id }));
        return {
            data: {
                report,
                total: Number(res.data?.total || 0),
            },
        };
    };

    if (!selectedDomain) return null;
    return (
        <Page
            title="Automations"
            subtitle="Build and manage your automation flows."
            primaryAction={{
                label: "New Automation",
                action: createNewAutomationAndOpenPanel,
            }}
            secondaryActions={[{
                label: "Refresh",
                action: () => setRefreshKey((v) => v + 1),
                isDisabled: !selectedDomain,
            }]}
        >
            <div className="v2-automations-table-scope">
                <Table
                    key={`v2-automations-table-${refreshKey}`}
                    title=""
                    fetch={fetchTableData}
                    cols={cols}
                    filters={tableFilters}
                    onRowClick={(row: V2AutomationListItemDto) => loadAutomationPanel(row.id)}
                    showSearch={false}
                    doneLoadingDependencies={!!selectedDomain}
                />
            </div>

            <AutomationTestLabModal
                open={testLabOpen}
                domain={selectedDomain}
                automationId={testLabAutomationId}
                onClose={() => {
                    setTestLabOpen(false);
                    setTestLabAutomationId(null);
                }}
            />

            <AutomationEditorPanel
                open={panelOpen}
                loading={panelLoading}
                saving={savingPanel}
                savingIndicator={savingPanel || autoSaving}
                isNewAutomation={isNewAutomationDraft}
                publishDisabled={isNewAutomationDraft || (!hasPublishableDraftChanges && !hasEntryConditionDraftChanges)}
                selectedAutomationId={selectedAutomationId}
                automationName={automationName}
                automationDescription={automationDescription}
                automationActive={automationActive}
                draftExitConditionId={draftExitConditionId}
                triggerMaxActiveRuns={triggerMaxActiveRuns}
                cooldownMode={cooldownMode}
                cooldownCustomValue={cooldownCustomValue}
                cooldownCustomUnit={cooldownCustomUnit}
                onClose={() => setPanelOpen(false)}
                onAutomationNameChange={setAutomationName}
                onAutomationDescriptionChange={setAutomationDescription}
                onAutomationActiveChange={updateAutomationActiveLive}
                onSelectCooldownPreset={(mode, seconds) => {
                    setCooldownMode(mode);
                    setTriggerCooldownSeconds(seconds);
                }}
                onSelectCustomCooldown={() => {
                    setCooldownMode("custom");
                    const split = splitCooldown(triggerCooldownSeconds);
                    setCooldownCustomValue(split.value);
                    setCooldownCustomUnit(split.unit);
                }}
                onCooldownCustomValueChange={(val) => {
                    setCooldownCustomValue(val);
                    setTriggerCooldownSeconds(toSeconds(val, cooldownCustomUnit));
                }}
                onCooldownCustomUnitChange={(unit) => {
                    setCooldownCustomUnit(unit);
                    setTriggerCooldownSeconds(toSeconds(cooldownCustomValue, unit));
                }}
                onTriggerMaxActiveRunsChange={setTriggerMaxActiveRuns}
                onOpenTree={() => {
                    if (selectedAutomationId) {
                        setTreeModalOpen(true);
                        return;
                    }
                    showToast({
                        skin: ToastSkin.neutral,
                        text: "To edit the tree, first save this automation. It still will not be published until you click Publish.",
                        buttons: [
                            {
                                text: "Save and continue to tree",
                                onClick: async () => {
                                    closeToast();
                                    const saved = await saveAutomationPanel({ allowCreate: true, refreshTable: true });
                                    if (saved.ok) setTreeModalOpen(true);
                                },
                            },
                            { text: "Cancel", onClick: closeToast },
                        ],
                    });
                }}
                onOpenTestLab={() => {
                    if (!selectedAutomationId) return;
                    setTestLabAutomationId(selectedAutomationId);
                    setTestLabOpen(true);
                }}
                onOpenExitCondition={openExitCondition}
                onDelete={deleteAutomationFromPanel}
                onSave={saveAutomationPanel}
                onPublish={publishFromPanel}
            />

            <Modal
                open={treeModalOpen}
                onCancel={() => {
                    setTreeModalOpen(false);
                    setTreeFullscreen(false);
                }}
                footer={null}
                width="96vw"
                className={`v2-automation-tree-modal ${treeFullscreen ? "tree-fullscreen" : ""}`}
                centered
                destroyOnClose
                zIndex={12000}
            >
                {selectedAutomationId && selectedDomain ? (
                    <AutomationTreeEditor
                        domain={selectedDomain}
                        automationId={selectedAutomationId}
                        showSettingsHeader={false}
                        showPublishInBoard={false}
                        stageHeight="88vh"
                        onFullscreenChange={setTreeFullscreen}
                        onDraftChanged={() => {
                            setHasPublishableDraftChanges(true);
                            setHasEntryConditionDraftChanges(true);
                        }}
                    />
                ) : null}
            </Modal>

            {selectedDomain && exitConditionModalId ? (
                <ConditionTreeEditorModal
                    domain={selectedDomain}
                    rootConditionId={exitConditionModalId}
                    open={exitConditionModalOpen}
                    onClose={() => setExitConditionModalOpen(false)}
                    onChanged={async () => {
                        setHasPublishableDraftChanges(true);
                    }}
                />
            ) : null}
        </Page>
    );
}
